import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { c, msgid, ngettext } from 'ttag';

import {
    Alert,
    AuthenticationStore,
    Button,
    LoaderPage,
    useAuthentication,
    useLocalState,
} from '@freelancelabs/components';

import {
    ActivationError,
    ActivationRefusal,
    ActivationResult,
    activateAccount,
    fetchActivationInvitee,
} from '../api/activateAccount';
import PublicLayout from '../app/content/PublicLayout';
import SetPasswordForm from '../app/login/SetPasswordForm';
import { PrivateRoutes, PublicRoutes, defaultUsernameKey } from '../constants';

// The screen asks the token, on load and without spending it, whether the invited person
// already has a password: an existing identity's password must never be reset. The decision
// is the invitee's real state, not the browser session. If the pool state moved since the
// pre-check, activateAccount answers PASSWORD_REQUIRED and the screen falls back to the form.
const ActivationPage = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [, setDefaultUsername] = useLocalState('', defaultUsernameKey);
    // The session no longer drives the path (the pre-check does); it only picks the
    // acknowledgement label: Continue when already signed in, else Sign in.
    const { hasSession } = useAuthentication() as AuthenticationStore;

    const [result, setResult] = useState<ActivationResult | null>(null);
    // Whether this activation set a password; drives the acknowledgement label.
    const [passwordSet, setPasswordSet] = useState(false);
    const [refusal, setRefusal] = useState<ActivationRefusal | null | undefined>(undefined);
    const [activating, setActivating] = useState(false);
    const [needsPassword, setNeedsPassword] = useState(false);
    const [invitee, setInvitee] = useState<{ hasPassword: boolean } | undefined>(undefined);

    useEffect(() => {
        if (!token) {
            return undefined;
        }
        let alive = true;
        void fetchActivationInvitee(token).then((answer) => {
            if (alive) {
                setInvitee(answer);
            }
        });
        return () => {
            alive = false;
        };
    }, [token]);

    /* ------------------------------------------------------------------ success */
    if (result) {
        const accounts = result.organizationNames;
        // Joined outside the template: ttag refuses a call expression inside a localised string.
        const accountList = accounts.join(', ');
        return (
            <PublicLayout>
                <h1 className="text-2xl font-semibold text-text-norm pb-4">
                    {passwordSet
                        ? c('Activation title').t`Your account is ready`
                        : c('Activation title').t`Your access is open`}
                </h1>
                <span className="text-sm pb-4">
                    {passwordSet
                        ? c('Activation')
                              .t`Your password is set. You can now sign in with your email address and this password.`
                        : // No-password path: nothing was set, so it is not mentioned.
                          c('Activation').t`Your accounts are now open. You can now sign in as usual.`}
                </span>
                {accounts.length > 0 && (
                    <span className="text-sm text-text-hint pb-2">
                        {ngettext(
                            msgid`This gives you access to ${accounts.length} account: ${accountList}.`,
                            `This gives you access to ${accounts.length} accounts: ${accountList}.`,
                            accounts.length
                        )}
                    </span>
                )}
                {hasSession ? (
                    // Already signed in: carried into the app via RedirectMiddleware, not to login.
                    <Link to={PrivateRoutes.HOMEPAGE} className="mt-4">
                        <Button size="large" fullWidth>
                            {c('Activation action').t`Continue`}
                        </Button>
                    </Link>
                ) : (
                    // Carried to sign-in with the address in the shared key, so the form opens filled.
                    <Link to={PublicRoutes.LOGIN} className="mt-4">
                        <Button size="large" fullWidth>
                            {c('Activation action').t`Sign in`}
                        </Button>
                    </Link>
                )}
            </PublicLayout>
        );
    }

    // `undefined` means nothing refused yet; `null` means refused but the API did not name it
    // (an unexpected failure), which must not be worded as a link problem.
    const terminal =
        refusal !== undefined &&
        refusal !== ActivationRefusal.PASSWORD_REJECTED &&
        refusal !== ActivationRefusal.PASSWORD_REQUIRED;
    if (!token || terminal) {
        return (
            <PublicLayout>
                <RefusalPanel
                    refusal={token ? refusal : ActivationRefusal.INVALID_LINK}
                    onRetry={() => setRefusal(undefined)}
                />
            </PublicLayout>
        );
    }

    if (invitee === undefined) {
        return <LoaderPage />;
    }

    // Existing identity with a password: don't reset it, a button just opens the memberships.
    if (invitee.hasPassword && !needsPassword) {
        return (
            <PublicLayout>
                <h1 className="text-xl font-semibold text-text-norm">{c('Activation title')
                    .t`Activate your access`}</h1>
                <span className="text-sm text-text-hint pb-6">
                    {c('Activation')
                        .t`This link opens your Freelance.com client area. You already have a Freelance.com account, so there is no new password to set: just activate your access.`}
                </span>

                <Button
                    size="large"
                    fullWidth
                    loading={activating}
                    onClick={async () => {
                        setActivating(true);
                        setRefusal(undefined);
                        try {
                            const activated = await activateAccount(token);
                            if (activated.email) {
                                setDefaultUsername(activated.email);
                            }
                            setResult(activated);
                        } catch (error) {
                            const code = error instanceof ActivationError ? error.code : null;
                            if (code === ActivationRefusal.PASSWORD_REQUIRED) {
                                setNeedsPassword(true);
                            } else {
                                setRefusal(code);
                            }
                        } finally {
                            setActivating(false);
                        }
                    }}
                >
                    {c('Activation action').t`Activate my access`}
                </Button>
            </PublicLayout>
        );
    }

    /* ---------------------------------------------------------------- the form */
    return (
        <PublicLayout>
            <h1 className="text-xl font-semibold text-text-norm">{c('Activation title').t`Choose your password`}</h1>
            <span className="text-sm text-text-hint pb-6">
                {c('Activation')
                    .t`This link opens your Freelance.com client area. Choose a password to finish setting up your access.`}
            </span>

            {/* Stays above the form: this refusal is repaired in the form itself. */}
            {refusal === ActivationRefusal.PASSWORD_REJECTED && (
                <Alert type="danger" className="mb-4">
                    {c('Activation refusal')
                        .t`This password was refused by our security policy. Choose a different one.`}
                </Alert>
            )}

            <SetPasswordForm
                onSubmit={async (password) => {
                    setRefusal(undefined);
                    try {
                        const activated = await activateAccount(token, password);
                        if (activated.email) {
                            setDefaultUsername(activated.email);
                        }
                        setPasswordSet(true);
                        setResult(activated);
                    } catch (error) {
                        setRefusal(error instanceof ActivationError ? error.code : null);
                        // Rethrown so the form drops its loading state; the message is already set.
                        throw error;
                    }
                }}
            />
        </PublicLayout>
    );
};

interface RefusalPanelProps {
    refusal: ActivationRefusal | null | undefined;
    onRetry: () => void;
}

// What to say and offer per refusal. Only a transient one is repaired by retry; an expired or
// revoked link carries no retry button. An expired link cannot be renewed from here (renewal is
// an operator's back-office gesture), so the screen names who can help instead.
const RefusalPanel = ({ refusal, onRetry }: RefusalPanelProps) => {
    const signIn = (
        <Link to={PublicRoutes.LOGIN} className="mt-6 block">
            <Button size="large" fullWidth>
                {c('Activation action').t`Sign in`}
            </Button>
        </Link>
    );

    if (refusal === ActivationRefusal.ALREADY_ACTIVATED) {
        return (
            <>
                <h1 className="text-2xl font-semibold text-text-norm pb-4">
                    {c('Activation title').t`This account is already active`}
                </h1>
                <span className="text-sm">
                    {c('Activation refusal')
                        .t`This invitation has already been used. Sign in with the password you chose.`}
                </span>
                {signIn}
            </>
        );
    }

    if (refusal === ActivationRefusal.IDENTITY_UNAVAILABLE) {
        // The one refusal the API names as transient: the token is unspent and the link survives.
        return (
            <>
                <h1 className="text-2xl font-semibold text-text-norm pb-4">
                    {c('Activation title').t`Activation is unavailable`}
                </h1>
                <span className="text-sm">
                    {c('Activation refusal')
                        .t`Your link is still valid — the service that sets passwords is momentarily unreachable. Try again in a few minutes.`}
                </span>
                <Button size="large" fullWidth className="mt-6" onClick={onRetry}>
                    {c('Activation action').t`Try again`}
                </Button>
            </>
        );
    }

    if (refusal === null) {
        // Refused without a known code (network failure, 500, early validation): not worded as a
        // link problem, nor claiming the link is fine, since nothing here established either.
        return (
            <>
                <h1 className="text-2xl font-semibold text-text-norm pb-4">
                    {c('Activation title').t`Something went wrong`}
                </h1>
                <span className="text-sm">
                    {c('Activation refusal')
                        .t`Your password could not be set. Nothing has been changed on your account, so you can try again. If it keeps failing, contact your administrator.`}
                </span>
                <Button size="large" fullWidth className="mt-6" onClick={onRetry}>
                    {c('Activation action').t`Try again`}
                </Button>
            </>
        );
    }

    if (refusal === ActivationRefusal.ACCOUNT_DISABLED) {
        return (
            <>
                <h1 className="text-2xl font-semibold text-text-norm pb-4">
                    {c('Activation title').t`This access has been closed`}
                </h1>
                <span className="text-sm">
                    {c('Activation refusal')
                        .t`Your access was closed before this link was used. Your administrator can reopen it.`}
                </span>
            </>
        );
    }

    if (refusal === ActivationRefusal.EXPIRED_LINK) {
        return (
            <>
                <h1 className="text-2xl font-semibold text-text-norm pb-4">
                    {c('Activation title').t`This link has expired`}
                </h1>
                <span className="text-sm">
                    {c('Activation refusal')
                        .t`Invitation links are valid for a limited time. Ask your administrator to send you a new one — the previous link stops working as soon as they do.`}
                </span>
            </>
        );
    }

    return (
        <>
            <h1 className="text-2xl font-semibold text-text-norm pb-4">
                {c('Activation title').t`This link is not valid`}
            </h1>
            <span className="text-sm">
                {c('Activation refusal')
                    .t`This invitation link cannot be used. It may have been replaced by a more recent one — check that you opened the latest email, or ask your administrator for a new invitation.`}
            </span>
        </>
    );
};

export default ActivationPage;
